import { useRef, useState, Children, isValidElement } from "react"
import { Send, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { ContactList } from "./contactList"
import { MessageSurface } from "./messageSurface"

export interface ChatMessage extends Message {
  status?: "sent" | "delivered" | "read"
  direction?: "left" | "right"
  contactObject?: Contact
}


function Toolbar({ children }: { children?: React.ReactNode }) {
  return <div className="flex items-center px-3 min-h-4 text-sm font-medium">{children}</div>
}

function ChatPanel({ children }: { children?: React.ReactNode }) {
  return <div className="flex-1 min-w-0 flex flex-col min-h-0">{children}</div>
}

function MessageInput({ children }: { children?: React.ReactNode }) {
  return <>{children}</>
}

function ContactInfo({ children }: { children?: React.ReactNode }) {
  return <>{children}</>
}

WhatsAppChat.Toolbar = Toolbar
WhatsAppChat.ChatPanel = ChatPanel
WhatsAppChat.MessageInput = MessageInput
WhatsAppChat.ContactInfo = ContactInfo

interface WhatsAppChatProps {
  className?: string
  children?: React.ReactNode
  contacts?: Contact[]
  messages?: ChatMessage[]
  selectedContactId?: any
  loadingContacts?: boolean
  loadingMessages?: boolean
  messagesProgress?: number
  emptyMessage?: string
  canSendMessage?: boolean
  hideContactName?: boolean
  onContactSelected?: (id: any) => void
  onSendMessage?: (contactId: string, content: string) => Promise<void>
  evaluationItems?: Record<string, Evaluation_Item>
}

export function WhatsAppChat({ className, evaluationItems, children, contacts = [], messages = [], selectedContactId = null, loadingContacts = false, loadingMessages = false, messagesProgress, emptyMessage = "Select a conversation to start chatting.", canSendMessage = true, hideContactName = false, onContactSelected, onSendMessage }: WhatsAppChatProps) {

  const [input, setInput] = useState("")
  const [sending, setSending] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const childArray = Children.toArray(children)
  const toolbarSlot = childArray.find(c => isValidElement(c) && c.type === Toolbar)
  const chatPanelSlot = childArray.find(c => isValidElement(c) && c.type === ChatPanel)
  const messageInputSlot = childArray.find(c => isValidElement(c) && c.type === MessageInput)
  const contactInfoSlot = childArray.find(c => isValidElement(c) && c.type === ContactInfo)

  const selectedContact = contacts.find(c => c.id == selectedContactId) ?? null
  // const chatMessages = messages.filter(m => m.contact == selectedContactId)
  const chatMessages = messages

  // console.log(chatMessages, selectedContactId, messages)

  async function handleSend() {
    if (!input.trim() || !selectedContactId || sending) return
    setSending(true)
    try {
      await onSendMessage?.(selectedContactId, input.trim())
    } finally {
      setSending(false)
    }
    setInput("")
    if (textareaRef.current) textareaRef.current.style.height = "auto"
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value)
    e.target.style.height = "auto"
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
  }

  const defaultChatPanel = <div className="flex-1 min-w-0 flex flex-col min-h-0">
    <MessageSurface
      selectedContact={selectedContact}
      messages={chatMessages}
      loading={loadingMessages}
      progress={messagesProgress}
      emptyMessage={emptyMessage}
      onDeselect={() => onContactSelected?.("")}
      contactInfo={contactInfoSlot ? (contactInfoSlot as React.ReactElement).props.children : undefined}
      hideContactName={hideContactName}
      evaluationItems={evaluationItems}
    />

    {selectedContact && canSendMessage && (
      messageInputSlot
        ? <div className="shrink-0 border-t border-border bg-background">{(messageInputSlot as React.ReactElement).props.children}</div>
        : <div className="flex items-end gap-2 px-3 py-3 border-t border-border bg-background shrink-0">
          <div className="flex-1 bg-muted rounded-lg px-3 py-2 border border-input">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type a message"
              rows={1}
              disabled={sending}
              className={cn("w-full bg-transparent text-foreground placeholder:text-muted-foreground text-sm resize-none outline-hidden leading-relaxed", sending && "opacity-50 cursor-not-allowed")}
              style={{ minHeight: "22px", maxHeight: "120px" }}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className={cn(
              "p-2.5 rounded-full transition-opacity shrink-0 mb-2",
              input.trim() || sending ? "bg-primary hover:opacity-90 text-primary-foreground" : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
    )}
  </div>

  return <div className={cn("flex flex-col h-full overflow-hidden bg-background rounded-lg", className)}>
    <div className="shrink-0 min-h-8">{toolbarSlot ?? <Toolbar />}</div>

    {/* Two-panel area */}
    <div className="flex flex-1 min-h-0 border border-border rounded-lg overflow-hidden">

      <ContactList
        contacts={contacts}
        selectedContactId={selectedContactId}
        loading={loadingContacts}
        onContactSelected={(id) => onContactSelected?.(id)}
      />

      {chatPanelSlot ?? defaultChatPanel}

    </div>
  </div>

}
